import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import {
  FaTimes,
  FaChartBar,
  FaSearch,
  FaFileExcel,
  FaSortUp,
  FaSortDown,
  FaSort,
  FaBoxOpen,
  FaTruckLoading,
  FaCheckCircle,
  FaWarehouse,
  FaLeaf,
  FaFire,
} from 'react-icons/fa';
import { db } from '../../config/firebaseConfig';
import {
  filterMovementsByDate,
  formatPercent,
  formatQuantity,
  summarizeByEmployee,
  summarizeByScope,
  summarizeByStock,
  summarizeTotals,
} from '../../utils/stockAnalytics';
import styles from './StockReportModal.module.css';

// Her sekme: hangi özeti ürettiği, hangi sütunları gösterdiği ve varsayılan
// sıralaması. Tablolar tek bir bileşenden çizildiği için sekme eklemek bu
// listeye bir satır eklemekten ibaret.
const TABS = [
  { key: 'employee', label: 'Çalışanlar', nameLabel: 'Çalışan' },
  { key: 'customer', label: 'Müşteriler', nameLabel: 'Müşteri' },
  { key: 'project', label: 'Projeler', nameLabel: 'Proje' },
  { key: 'stock', label: 'Malzemeler', nameLabel: 'Malzeme' },
];

const COLUMNS = {
  employee: [
    { key: 'employeeName', label: 'Çalışan', type: 'text' },
    { key: 'assigned', label: 'Verilen', type: 'number' },
    { key: 'used', label: 'Kullanılan', type: 'number' },
    { key: 'onHand', label: 'Elinde', type: 'number' },
    { key: 'usageRate', label: 'Kullanım', type: 'rate' },
    { key: 'visitCount', label: 'Vizit', type: 'number' },
    { key: 'perVisit', label: 'Vizit başına', type: 'number' },
  ],
  customer: [
    { key: 'label', label: 'Müşteri', type: 'text' },
    { key: 'assigned', label: 'Gönderilen', type: 'number' },
    { key: 'used', label: 'Kullanılan', type: 'number' },
    { key: 'onHand', label: 'Sahada', type: 'number' },
    { key: 'remaining', label: 'Depoda kalan', type: 'number' },
    { key: 'usageRate', label: 'Kullanım', type: 'rate' },
    { key: 'stockCount', label: 'Malzeme', type: 'number' },
  ],
  project: [
    { key: 'label', label: 'Proje', type: 'text' },
    { key: 'assigned', label: 'Gönderilen', type: 'number' },
    { key: 'used', label: 'Kullanılan', type: 'number' },
    { key: 'onHand', label: 'Sahada', type: 'number' },
    { key: 'remaining', label: 'Depoda kalan', type: 'number' },
    { key: 'usageRate', label: 'Kullanım', type: 'rate' },
    { key: 'stockCount', label: 'Malzeme', type: 'number' },
  ],
  stock: [
    { key: 'stockName', label: 'Malzeme', type: 'text' },
    { key: 'customerName', label: 'Müşteri', type: 'text' },
    { key: 'projectName', label: 'Proje', type: 'text' },
    { key: 'assigned', label: 'Dağıtılan', type: 'number' },
    { key: 'used', label: 'Kullanılan', type: 'number' },
    { key: 'remaining', label: 'Depoda kalan', type: 'number' },
    { key: 'employeeCount', label: 'Kişi', type: 'number' },
  ],
};

const DEFAULT_SORT = {
  employee: 'assigned',
  customer: 'assigned',
  project: 'assigned',
  stock: 'used',
};

const searchableText = (row) =>
  [row.employeeName, row.label, row.stockName, row.customerName, row.projectName]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase('tr-TR');

/**
 * Stok kullanım raporu.
 *
 * "Kime ne kadar verildi, ne kadar kullanıldı, ne kadar kaldı" sorusunu tek
 * ekranda cevaplar. Veri iki kaynaktan gelir: StockMovements (atama + vizit
 * kullanımı) ve Stocks (depodaki güncel kalan).
 */
const StockReportModal = ({ show, onClose, isDarkMode = false }) => {
  const [movements, setMovements] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('employee');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [onlyConsumables, setOnlyConsumables] = useState(true);
  const [sort, setSort] = useState({ key: DEFAULT_SORT.employee, direction: 'desc' });

  useEffect(() => {
    if (!show) return undefined;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [movementSnap, stockSnap] = await Promise.all([
          getDocs(collection(db, 'StockMovements')),
          getDocs(collection(db, 'Stocks')),
        ]);
        if (cancelled) return;
        setMovements(movementSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setStocks(stockSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error('Stok raporu yüklenemedi:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [show]);

  // Sarf malzemesi süzgeci: müşteri/proje bağlantısı yalnızca sarf için var
  const scopedStocks = useMemo(
    () => (onlyConsumables ? stocks.filter((s) => s['Kategori'] === 'sarf') : stocks),
    [stocks, onlyConsumables]
  );

  const scopedMovements = useMemo(() => {
    const dated = filterMovementsByDate(movements, startDate, endDate);
    if (!onlyConsumables) return dated;

    const consumableIds = new Set(scopedStocks.map((s) => s.id));
    const knownIds = new Set(stocks.map((s) => s.id));
    // Stok kartı silinmiş hareketlerin kategorisi bilinemez; rapordan
    // düşürmek yerine gösteriyoruz ki geçmiş tüketim kaybolmasın.
    return dated.filter(
      (m) => !m.stockId || consumableIds.has(m.stockId) || !knownIds.has(m.stockId)
    );
  }, [movements, scopedStocks, stocks, startDate, endDate, onlyConsumables]);

  const totals = useMemo(
    () => summarizeTotals(scopedMovements, scopedStocks),
    [scopedMovements, scopedStocks]
  );

  const rows = useMemo(() => {
    switch (activeTab) {
      case 'customer':
        return summarizeByScope(scopedMovements, scopedStocks, 'customer');
      case 'project':
        return summarizeByScope(scopedMovements, scopedStocks, 'project');
      case 'stock':
        return summarizeByStock(scopedMovements, scopedStocks);
      case 'employee':
      default:
        return summarizeByEmployee(scopedMovements);
    }
  }, [activeTab, scopedMovements, scopedStocks]);

  const visibleRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr-TR');
    const filtered = term ? rows.filter((row) => searchableText(row).includes(term)) : rows;

    const column = COLUMNS[activeTab].find((c) => c.key === sort.key);
    const direction = sort.direction === 'asc' ? 1 : -1;

    return [...filtered].sort((a, b) => {
      const aValue = a[sort.key];
      const bValue = b[sort.key];
      if (column?.type === 'text') {
        return direction * String(aValue || '').localeCompare(String(bValue || ''), 'tr');
      }
      return direction * ((Number(aValue) || 0) - (Number(bValue) || 0));
    });
  }, [rows, search, sort, activeTab]);

  // "Kim daha dikkatli kullanıyor": vizit başına en az tüketen. Karşılaştırma
  // ancak vizit yapmış kişiler arasında anlamlı olduğu için diğerleri elenir.
  const highlights = useMemo(() => {
    if (activeTab !== 'employee') return null;
    // employeeId taşımayan hareketler "Bilinmeyen çalışan" altında toplanır;
    // bu gerçek bir kişi olmadığı için kıyaslamaya girmez.
    const withVisits = rows.filter(
      (row) => row.employeeId && row.visitCount > 0 && row.used > 0
    );
    if (withVisits.length < 2) return null;
    const sorted = [...withVisits].sort((a, b) => a.perVisit - b.perVisit);
    return { careful: sorted[0], heavy: sorted[sorted.length - 1] };
  }, [rows, activeTab]);

  const toggleSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
        : { key, direction: 'desc' }
    );
  };

  const changeTab = (key) => {
    setActiveTab(key);
    setSort({ key: DEFAULT_SORT[key], direction: 'desc' });
  };

  const exportExcel = () => {
    const columns = COLUMNS[activeTab];
    const data = visibleRows.map((row) => {
      const record = {};
      columns.forEach((column) => {
        record[column.label] =
          column.type === 'rate' ? formatPercent(row[column.key]) : row[column.key] ?? '';
      });
      return record;
    });
    const sheet = XLSX.utils.json_to_sheet(data);
    const book = XLSX.utils.book_new();
    const tabLabel = TABS.find((t) => t.key === activeTab)?.label || 'Rapor';
    XLSX.utils.book_append_sheet(book, sheet, tabLabel);
    XLSX.writeFile(book, `Stok_Raporu_${tabLabel}.xlsx`);
  };

  if (!show) return null;

  const columns = COLUMNS[activeTab];

  const renderCell = (row, column) => {
    const value = row[column.key];

    if (column.type === 'rate') {
      const percent = Math.min(100, Math.round((value || 0) * 100));
      return (
        <div className={styles.rateCell} title={`${formatPercent(value)} kullanıldı`}>
          <div className={styles.rateTrack}>
            <div
              className={`${styles.rateFill} ${percent >= 90 ? styles.rateFillHigh : ''}`}
              style={{ width: `${percent}%` }}
            />
          </div>
          <span className={styles.rateValue}>{formatPercent(value)}</span>
        </div>
      );
    }

    if (column.type === 'number') {
      return <span className={styles.numberCell}>{formatQuantity(value)}</span>;
    }

    // Malzeme sekmesinde düşük stok uyarısı ada bitişik durur
    if (column.key === 'stockName' && row.isLow) {
      return (
        <span className={styles.nameCell}>
          {value || '-'}
          <span className={styles.lowBadge}>Kritik</span>
        </span>
      );
    }

    return <span className={styles.nameCell}>{value || '—'}</span>;
  };

  return (
    <div
      className={`${styles.overlay} ${isDarkMode ? styles.dark : ''}`}
      onClick={onClose}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <FaChartBar className={styles.headerIcon} />
            <div>
              <h2>Stok Kullanım Raporu</h2>
              <span className={styles.headerSubtitle}>
                Kime ne kadar verildi, ne kadar kullanıldı, ne kadar kaldı
              </span>
            </div>
          </div>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Kapat">
            <FaTimes />
          </button>
        </div>

        {/* Özet şerit */}
        <div className={styles.summaryRow}>
          <div className={styles.summaryCard}>
            <FaTruckLoading className={styles.summaryIcon} />
            <div>
              <span className={styles.summaryValue}>{formatQuantity(totals.assigned)}</span>
              <span className={styles.summaryLabel}>Dağıtılan</span>
            </div>
          </div>
          <div className={`${styles.summaryCard} ${styles.summaryUsed}`}>
            <FaCheckCircle className={styles.summaryIcon} />
            <div>
              <span className={styles.summaryValue}>{formatQuantity(totals.used)}</span>
              <span className={styles.summaryLabel}>Kullanılan</span>
            </div>
          </div>
          <div className={`${styles.summaryCard} ${styles.summaryOnHand}`}>
            <FaBoxOpen className={styles.summaryIcon} />
            <div>
              <span className={styles.summaryValue}>{formatQuantity(totals.onHand)}</span>
              <span className={styles.summaryLabel}>Sahada</span>
            </div>
          </div>
          <div className={`${styles.summaryCard} ${styles.summaryRemaining}`}>
            <FaWarehouse className={styles.summaryIcon} />
            <div>
              <span className={styles.summaryValue}>{formatQuantity(totals.remaining)}</span>
              <span className={styles.summaryLabel}>Depoda kalan</span>
            </div>
          </div>
        </div>

        {/* Sekmeler ve süzgeçler */}
        <div className={styles.toolbar}>
          <div className={styles.tabs}>
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
                onClick={() => changeTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className={styles.filters}>
            <label className={styles.consumableToggle} title="Yalnızca sarf malzemelerini raporla">
              <input
                type="checkbox"
                checked={onlyConsumables}
                onChange={(e) => setOnlyConsumables(e.target.checked)}
              />
              Sadece sarf
            </label>
            <input
              type="date"
              className={styles.dateInput}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              title="Başlangıç tarihi"
            />
            <input
              type="date"
              className={styles.dateInput}
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
              title="Bitiş tarihi"
            />
            <div className={styles.searchBox}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="button" className={styles.exportButton} onClick={exportExcel}>
              <FaFileExcel /> Excel
            </button>
          </div>
        </div>

        {/* Öne çıkanlar */}
        {highlights && (
          <div className={styles.highlights}>
            <div className={`${styles.highlight} ${styles.highlightCareful}`}>
              <FaLeaf className={styles.highlightIcon} />
              <span>
                <strong>{highlights.careful.employeeName}</strong> en tutumlu kullanıyor —
                vizit başına {formatQuantity(highlights.careful.perVisit)} adet
              </span>
            </div>
            <div className={`${styles.highlight} ${styles.highlightHeavy}`}>
              <FaFire className={styles.highlightIcon} />
              <span>
                <strong>{highlights.heavy.employeeName}</strong> en çok tüketiyor —
                vizit başına {formatQuantity(highlights.heavy.perVisit)} adet
              </span>
            </div>
          </div>
        )}

        {/* Tablo */}
        <div className={styles.tableWrapper}>
          {loading ? (
            <div className={styles.stateBox}>
              <span className={styles.spinner} />
              <p>Rapor hazırlanıyor...</p>
            </div>
          ) : visibleRows.length === 0 ? (
            <div className={styles.stateBox}>
              <FaBoxOpen className={styles.stateIcon} />
              <p>Seçilen aralıkta kayıt bulunmuyor.</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={column.type === 'text' ? '' : styles.thNumeric}
                      onClick={() => toggleSort(column.key)}
                    >
                      <span className={styles.thContent}>
                        {column.label}
                        {sort.key === column.key ? (
                          sort.direction === 'desc' ? <FaSortDown /> : <FaSortUp />
                        ) : (
                          <FaSort className={styles.sortIdle} />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.key}>
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={column.type === 'text' ? '' : styles.tdNumeric}
                      >
                        {renderCell(row, column)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className={styles.footer}>
          <span>{visibleRows.length} kayıt</span>
          <span className={styles.footerHint}>
            Vizit başına ortalama düştükçe kullanım daha tutumlu demektir.
          </span>
        </div>
      </div>
    </div>
  );
};

export default StockReportModal;
